import { makeAutoObservable, runInAction } from "mobx";
import { colorForTag, normalizeTags } from "../../components/TagPicker";
import { BrokerService } from "./BrokerService";

/**
 * In-memory directory of broker-managed tags. It reads the broker-scoped tag
 * registry (the shared `/api/tag-element` collection, auto-scoped to the broker)
 * and also records tags found on grid/detail payloads so older assignments remain
 * selectable if the registry is missing a record. FV Bank's internal `tags`
 * registry is intentionally not exposed here.
 */
class BrokerTagStore {
  byId = {};
  loaded = false;
  loading = false;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  recordElement(record) {
    if (!record?._id) return;
    this.byId[String(record._id)] = {
      tag_id: record._id,
      details: record.label || record.details || record._id,
      color: record.color || colorForTag(record._id),
    };
  }

  record(tags) {
    (Array.isArray(tags) ? tags : []).forEach((tag) => {
      if (tag && tag.tag_id) {
        const existing = this.byId[String(tag.tag_id)] || {};
        this.byId[String(tag.tag_id)] = {
          tag_id: tag.tag_id,
          details: tag.details || tag.label || tag.name || tag.tag_id,
          color: tag.color || existing.color || colorForTag(tag.tag_id),
        };
      }
    });
  }

  // Aggregate brokerTags from a list of grid rows / application records.
  recordFromRows(rows) {
    (Array.isArray(rows) ? rows : []).forEach((row) => {
      this.record(row?.brokerTags || row?.application?.brokerTags);
    });
  }

  async fetch(force = false) {
    if (this.loaded && !force) return this.options;
    if (this.loading && !force) return this.options;
    this.loading = true;
    try {
      const response = await BrokerService.brokerTagElements();
      runInAction(() => {
        (response?.data?.rows || []).forEach((record) => this.recordElement(record));
        this.loaded = true;
      });
    } finally {
      runInAction(() => { this.loading = false; });
    }
    return this.options;
  }

  async ensureRegisteredTags(tags = []) {
    await this.fetch();
    const normalized = normalizeTags(tags);
    const byLabel = () => Object.values(this.byId).reduce((acc, tag) => {
      acc[String(tag.details || "").trim().toLowerCase()] = tag;
      return acc;
    }, {});
    let labelLookup = byLabel();
    const assignments = [];

    for (const tag of normalized) {
      if (this.byId[String(tag.tag_id)]) {
        const record = this.byId[String(tag.tag_id)];
        assignments.push({ tag_id: record.tag_id, details: record.details, color: record.color });
        continue;
      }

      const label = String(tag.details || tag.label || tag.name || tag.tag_id || "").trim();
      if (!label) continue;

      const existing = labelLookup[label.toLowerCase()];
      if (existing) {
        assignments.push({ tag_id: existing.tag_id, details: existing.details, color: existing.color });
        continue;
      }

      const color = tag.color || colorForTag(tag);
      const response = await BrokerService.createBrokerTagElement({ label, color });
      const created = response?.data;
      if (created?._id) {
        runInAction(() => this.recordElement(created));
        labelLookup = byLabel();
        assignments.push({ tag_id: created._id, details: created.label || label, color: created.color || color });
      }
    }

    return assignments;
  }

  // Registry management — propagate across the broker's applications.
  async renameElement(tag, label) {
    const response = await BrokerService.updateBrokerTagElement(tag.tag_id, { label, color: tag.color });
    const updated = response?.data;
    if (updated?._id) runInAction(() => this.recordElement(updated));
    return updated;
  }

  async recolorElement(tag, color) {
    const response = await BrokerService.updateBrokerTagElement(tag.tag_id, { label: tag.details, color });
    const updated = response?.data;
    if (updated?._id) runInAction(() => this.recordElement(updated));
    return updated;
  }

  async deleteElement(tag) {
    await BrokerService.deleteBrokerTagElement(tag.tag_id);
    runInAction(() => { delete this.byId[String(tag.tag_id)]; });
  }

  get options() {
    return Object.values(this.byId).sort((a, b) => String(a.details).localeCompare(String(b.details)));
  }
}

export const brokerTagStore = new BrokerTagStore();

export default brokerTagStore;
